import React, { useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { MenuType } from 'src/components/common/MenuItemComponent';
import { useTranslations } from 'src/components/hooks/useTranslations';
import { useLazyGetSiteStudyPersonnelDataQuery } from 'src/services/SiteContactInfo';
import { useCreateUserClickMutation, useGetUserClicksQuery } from 'src/services/Sites';
import { useLazyGetStudiesQuery, utilsApi } from 'src/services/Utils';
import { hideLoader, showLoader } from 'src/store/Loader';
import { clearRoles } from 'src/store/Roles';
import {
  clearCountrySiteOnly,
  clearSiteOnly,
  clearStudySite,
  setCountry,
  setCurrentHierarchyLevel,
  setHierarchyLevelClosed,
  setSite,
  setSiteClicksData,
  setSiteInfo,
  setSitePermissionData,
  setSiteRole,
  setStudy
} from 'src/store/Site';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { ITabLevelHierarchy } from 'src/types/TabsType';
import { Translation } from 'src/types/Translation';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import { updateUserAccess } from 'src/util/util';
import { useUser } from '../hooks/useUser';
import { getPoratlHost } from '../hooks/useUtil';
import { getSearchHierarchyInitialData } from './util';

export interface resData {
  name: string;
  role: string;
  sponsor_name: string;
}
export interface IMenu {
  label: string;
  value: string;
  options: string[];
  resData: resData[];
  menuType: MenuType;
}

export interface IMenus {
  sponsor: IMenu;
  study: IMenu;
  country: IMenu;
  site: IMenu;
  tabHierarchyLevels: ITabLevelHierarchy;
}

export interface IMenuAction {
  menu: IMenus;
  onChange: (item: string, type: MenuType) => void;
}

export interface IPortalProps {
  study_id: string;
  sponsor_name: string;
}
export const useSearchHierarchy = () => {
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const hierarchyConfigs = useTranslations(
    {} as Translation,
    { returnObjects: true },
    'hierarchyConfigs'
  ).hierarchyConfigs;
  const ptnStudyCountrySite: any = useAppSelector(state => state.selectedHierarchy);
  const { selectedApp } = useSelectedAppInfo();
  const siteClicksData: any = useAppSelector(state => state.site.siteClicksData);
  const selectedCountry: string | null = useAppSelector(state => state.site.selectedCountry);
  const isHierarchyLevelClosed: boolean = useAppSelector(state => state.site.isHierarchyLevelClosed);
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const selectedStudy: string | null = useAppSelector(state => state.site.selectedStudy);
  const userGroups = useAppSelector(state => state.user.user?.roles);
  const portalProps: Partial<IPortalProps> = useAppSelector(state => state.user.user?.portal_props);
  const { error: userClicksError, data: userClicksData, isLoading: userClicksLoading } = useGetUserClicksQuery({});
  const [studyRefetch, { data: studyData, isLoading: studyLoading, error: studyError }] = useLazyGetStudiesQuery();
  useEffect(() => {
    if (userClicksData) {
      const temData: any = [
        {
          site_id: userClicksData.site_id,
          count: userClicksData.click_count,
          study_id: userClicksData.study_id,
          country_id: userClicksData.country_id
        }
      ];
      dispatch(setSiteClicksData(temData));
      studyRefetch();
    }
  }, [userClicksData]);

  useEffect(() => {
    if (userClicksError) {
      const temData: any = [];
      dispatch(setSiteClicksData(temData));
      studyRefetch();
    }
  }, [userClicksError]);

  const [createClick] = useCreateUserClickMutation();

  const userSiteClicksUpdate = (site_id: string) => {
    const temData: any = [];
    let foundSite = null;

    if (siteClicksData) {
      foundSite = siteClicksData.find((item: any) => item.site_id == site_id);
    }
    if (foundSite) {
      createClick({
        data: {
          site_id,
          study_id: selectedStudy,
          country_id: selectedCountry,
          click_count: foundSite.count + 1,
          created_by: user
        }
      });
      if (siteClicksData) {
        siteClicksData.forEach((item: any) => {
          if (item.site_id == site_id) {
            temData.push({ site_id, count: item.count + 1 });
          } else {
            temData.push(item);
          }
        });
      }
    } else {
      createClick({
        data: {
          site_id,
          study_id: selectedStudy,
          country_id: selectedCountry,
          click_count: 1,
          created_by: user
        }
      });
      if (siteClicksData) {
        siteClicksData.forEach((item: any) => {
          temData.push(item);
        });
      }
      temData.push({ site_id, count: 1, study_id: selectedStudy, country_id: selectedCountry });
    }

    dispatch(setSiteClicksData(temData));
  };

  const [sponsorStudyCountrySite, setSponsorStudyCountrySite] = useState<IMenus>(
    getSearchHierarchyInitialData(hierarchyConfigs)
  );

  useEffect(() => {
    let study: string[] = [];
    //if (sponsorStudyCountrySite.sponsor.value.length > 0) {
    let roleBasedStudyData: any = [];
    if (studyData) {
      /*To filter only user role based data */
      roleBasedStudyData = studyData.filter((item: any) => {
        if (userGroups.includes(item.role)) {
          return item;
        }
      });
      const studyStrings = roleBasedStudyData.map((obj: any) => obj.name);
      study = Array.isArray(studyStrings) ? studyStrings : [];
      if (portalProps?.study_id) {
        studyUpdate(portalProps.study_id, [], [], roleBasedStudyData);
      } else if (ptnStudyCountrySite.hierarchyStudy !== '') {
        studyUpdate(ptnStudyCountrySite.hierarchyStudy, [], [], roleBasedStudyData);
      } else if (siteClicksData && study?.includes(siteClicksData[0]?.study_id)) {
        studyUpdate(siteClicksData[0]?.study_id, [], [], roleBasedStudyData);
      } else {
        studyUpdate(study[0], [], [], roleBasedStudyData);
      }
      dispatch(setSitePermissionData(true));
    }
    //}
    setSponsorStudyCountrySite(prev => {
      return {
        ...prev,
        study: { ...prev.study, options: study, resData: roleBasedStudyData }
      };
    });
  }, [studyData, ptnStudyCountrySite.hierarchyStudy]);

  const studyCountryResult = React.useMemo(
    () => utilsApi.endpoints.getCountries.select(sponsorStudyCountrySite.study.value),
    [sponsorStudyCountrySite.study.value]
  );

  const {
    isLoading: studyCountryLoading,
    data: studyCountryData,
    error: studyCountryError
  } = useAppSelector(studyCountryResult);

  useEffect(() => {
    if (sponsorStudyCountrySite.study.value && sponsorStudyCountrySite.study.value.length > 0) {
      const result = dispatch(utilsApi.endpoints.getCountries.initiate(sponsorStudyCountrySite.study.value));
      return result.unsubscribe;
    }
  }, [dispatch, sponsorStudyCountrySite.study.value]);

  useEffect(() => {
    let countries: string[] = [];
    let roleBasedCountryData: any = [];
    if (sponsorStudyCountrySite.study.value && sponsorStudyCountrySite.study.value.length > 0 && studyCountryData) {
      /*To filter only user role based data */
      roleBasedCountryData = studyCountryData.filter((item: any) => {
        if (userGroups.includes(item.role)) {
          return item;
        }
      });
      const countryStrings = roleBasedCountryData.map((obj: any) => obj.name);
      countries = Array.isArray(countryStrings) ? countryStrings : [];
      if (!isHierarchyLevelClosed) {
        if (siteClicksData && countries?.includes(siteClicksData[0]?.country_id)) {
          countryUpdate(siteClicksData[0]?.country_id, roleBasedCountryData);
        } else {
          countryUpdate(countries[0], roleBasedCountryData);
        }
      }
      dispatch(setSitePermissionData(true));
    }
    setSponsorStudyCountrySite(prev => {
      return {
        ...prev,
        country: { ...prev.country, options: countries, resData: roleBasedCountryData }
      };
    });
  }, [sponsorStudyCountrySite.study.value, studyCountryData]);

  const studyCountrySiteResult = React.useMemo(
    () =>
      utilsApi.endpoints.getSites.select({
        currentStudy: sponsorStudyCountrySite.study.value,
        currentCountry: sponsorStudyCountrySite.country.value
      }),

    [sponsorStudyCountrySite.country.value]
  );

  const {
    isLoading: studyCountrySiteLoading,
    data: studyCountrySiteData,
    error: studyCountrySiteError
  } = useAppSelector(studyCountrySiteResult);

  useEffect(() => {
    if (sponsorStudyCountrySite.country.value && sponsorStudyCountrySite.country.value.length > 0) {
      const result = dispatch(
        utilsApi.endpoints.getSites.initiate({
          currentStudy: sponsorStudyCountrySite.study.value,
          currentCountry: sponsorStudyCountrySite.country.value
        })
      );
      return result.unsubscribe;
    }
  }, [dispatch, sponsorStudyCountrySite.country.value]);

  useEffect(() => {
    let sites: string[] = [];
    let roleBasedSiteData: any = [];
    if (sponsorStudyCountrySite.study.value && sponsorStudyCountrySite.study.value.length > 0 && studyCountrySiteData) {
      /*To filter only user role based data */
      roleBasedSiteData = studyCountrySiteData.filter((item: any) => {
        if (userGroups.includes(item.role)) {
          return item;
        }
      });

      const siteStrings = roleBasedSiteData.map((obj: any) => obj.name);
      sites = Array.isArray(siteStrings) ? siteStrings : [];

      let siteId = sites[0];

      /**
       * Find most clicked site start
       */
      if (sites.includes(siteClicksData[0]?.site_id)) {
        siteId = siteClicksData[0]?.site_id;
      }
      /**
       * Find most clicked site End
       */
      if (!isHierarchyLevelClosed) {
        siteUpdate(siteId, roleBasedSiteData);
      }
      dispatch(setSitePermissionData(true));
    } else if (currentHierarchyLevel !== 'study') {
      // dispatch(setSiteRole(null));
    }
    setSponsorStudyCountrySite(prev => {
      return {
        ...prev,
        site: { ...prev.site, options: sites, resData: roleBasedSiteData }
      };
    });
  }, [sponsorStudyCountrySite.country.value, studyCountrySiteData]);
  const [loading, setLoading] = useState(false);

  const { tab: tabVal, tabsData } = useAppSelector(state => state.tabs);
  const [hierarchyVisibility, setHierarchyVisibility] = useState<any>([]);

  useEffect(() => {
    if (tabVal > -1 && tabsData.length > 0) {
      const selectedTabObj: any = tabsData.find((tab: any) => tab.index === tabVal);
      if (selectedTabObj?.showHierarchy) {
        setHierarchyVisibility(selectedTabObj?.showHierarchy);
      } else {
        setHierarchyVisibility({});
      }
    }
  }, [tabVal, tabsData]);

  useEffect(() => {
    if (studyCountrySiteLoading || studyCountryLoading || studyLoading || userClicksLoading) {
      if (!hierarchyVisibility?.length) {
        dispatch(showLoader());
        setLoading(true);
      }
    } else {
      dispatch(hideLoader());
      setLoading(false);
    }
  }, [studyCountrySiteLoading, studyCountryLoading, studyLoading, userClicksLoading]);

  useEffect(() => {
    if (studyCountrySiteError) {
      updateUserAccess(studyCountrySiteError, dispatch);
    }
  }, [studyCountrySiteError]);

  useEffect(() => {
    if (studyCountryError) {
      updateUserAccess(studyCountryError, dispatch);
    }
  }, [studyCountryError]);

  useEffect(() => {
    if (studyError) {
      updateUserAccess(studyError, dispatch);
      batch(() => {
        dispatch(clearStudySite());
        dispatch(clearRoles());
        dispatch(setSitePermissionData(false));
      });
    }
  }, [studyError]);

  const studyUpdate = (item: string, countries: string[], sites: string[], resData: resData[]) => {
    let findSiteRole: any = {};
    let selectedSponsor = '';
    if (item === null || item === '') {
      batch(() => {
        dispatch(setHierarchyLevelClosed(true));
        dispatch(setStudy(null));
        dispatch(setCountry(null));
        dispatch(setSite(null));
        dispatch(setCurrentHierarchyLevel(null));
        // dispatch(clearSiteRole());
      });
    } else {
      selectedSponsor = resData?.filter((studyInfo: any) => studyInfo.name === item)[0]?.sponsor_name;
      window.parent.postMessage(
        { action: 'SPONSOR_NAME', slug_id: selectedApp, selected_sponsor: selectedSponsor },
        getPoratlHost()
      );
      findSiteRole = resData?.find((obj: resData) => obj.name == item);
      dispatch(setCurrentHierarchyLevel('study'));
      batch(() => {
        dispatch(setHierarchyLevelClosed(false));
        dispatch(setStudy(item));
        dispatch(clearCountrySiteOnly());
        dispatch(setSiteRole(findSiteRole?.role));
      });
    }

    setSponsorStudyCountrySite(prev => {
      return {
        ...prev,
        sponsor: { ...prev.sponsor, value: selectedSponsor },
        study: { ...prev.study, value: item },
        country: { ...prev.country, value: '', options: countries },
        site: { ...prev.site, value: '', options: sites }
      };
    });
  };

  const countryUpdate = (item: string, resData: resData[]) => {
    let findSiteRole: any = {};
    setSponsorStudyCountrySite(prev => {
      return {
        ...prev,
        country: { ...prev.country, value: item },
        site: { ...prev.site, value: '' }
      };
    });
    if (item === null || item === '') {
      findSiteRole = sponsorStudyCountrySite?.study?.resData?.find((obj: resData) => obj.name == selectedStudy);
      batch(() => {
        dispatch(setSiteRole(findSiteRole?.role));
        dispatch(setCountry(null));
        dispatch(setSite(null));
        dispatch(setHierarchyLevelClosed(true));
        dispatch(setCurrentHierarchyLevel('study'));
      });
    } else {
      findSiteRole = resData.find((obj: resData) => obj.name == item);
      dispatch(setCurrentHierarchyLevel('country'));
      batch(() => {
        dispatch(setHierarchyLevelClosed(false));
        dispatch(setCountry(item));
        dispatch(clearSiteOnly());
        dispatch(setSiteRole(findSiteRole?.role));
      });
    }
  };

  const [
    getSiteStudyPersonnelQueryData,
    {
      data: siteStudyPersonnelQueryData,
      isLoading: siteStudyPersonnelQueryDataLoading,
      isSuccess: siteStudyPersonnelQueryDataIsSuccess
    }
  ] = useLazyGetSiteStudyPersonnelDataQuery();

  useEffect(() => {
    if (siteStudyPersonnelQueryData && !siteStudyPersonnelQueryDataLoading && siteStudyPersonnelQueryDataIsSuccess) {
      dispatch(setSiteInfo(siteStudyPersonnelQueryData));
    }
  }, [siteStudyPersonnelQueryData, siteStudyPersonnelQueryDataLoading, siteStudyPersonnelQueryDataIsSuccess]);

  const siteUpdate = (item: string, resData: resData[]) => {
    hierarchyUpdate('site', item);
    let findSiteRole: any = {};
    if (item === null || item === '') {
      findSiteRole = sponsorStudyCountrySite?.country?.resData?.find((obj: resData) => obj.name == selectedCountry);
      batch(() => {
        dispatch(setHierarchyLevelClosed(true));
        dispatch(setSite(null));
        dispatch(setSiteRole(findSiteRole?.role));
        dispatch(setCurrentHierarchyLevel('country'));
      });
    } else {
      findSiteRole = resData.find((obj: resData) => obj.name == item);
      dispatch(setCurrentHierarchyLevel('site'));
      batch(() => {
        dispatch(setHierarchyLevelClosed(false));
        dispatch(setSite(item));
        dispatch(setSiteRole(findSiteRole?.role));
        setConfirmExitValue(dispatch, false);
      });

      getSiteStudyPersonnelQueryData({
        site_id: item,
        study_id: selectedStudy,
        country: selectedCountry,
        application: 'CTMS'
      });
    }
    userSiteClicksUpdate(item);
  };
  const hierarchyUpdate = (key: keyof IMenus, item: string) => {
    setSponsorStudyCountrySite(prev => {
      return {
        ...prev,
        [key]: { ...prev[key], value: item }
      };
    });
  };

  const sponsorUpdate = (item: string) => {
    const defaultValues = getSearchHierarchyInitialData(hierarchyConfigs);
    defaultValues.sponsor.value = item;
    setSponsorStudyCountrySite(defaultValues);
  };
  const selectionChanged = (item: string, type: MenuType, resData: resData[]) => {
    switch (type) {
      case MenuType.Sponsor:
        sponsorUpdate(item);
        break;
      case MenuType.Study:
        if (sponsorStudyCountrySite?.study?.value !== item) {
          studyUpdate(item, [], [], resData);
        }
        break;
      case MenuType.Country:
        if (sponsorStudyCountrySite?.country?.value !== item) {
          countryUpdate(item, resData);
        }
        break;
      case MenuType.Site:
        if (sponsorStudyCountrySite?.site?.value !== item) {
          siteUpdate(item, resData);
        }
        break;
    }
  };
  const onChange = (item: string, type: MenuType, resData: resData[]) => {
    selectionChanged(item, type, resData);
  };
  return { onChange, sponsorStudyCountrySite, loading, hierarchyVisibility };
};
